import React, {
  MouseEvent,
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'
import {
  FieldArray,
  FieldArrayRenderProps,
  FormikHelpers,
  FormikProps,
  useField,
} from 'formik'
import clsx from 'clsx'
import { CheckboxOptionType, CheckboxValueType } from 'antd/lib/checkbox/Group'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import {
  CheckCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  PlusOutlined,
  FacebookFilled,
  LinkedinFilled,
  TwitterSquareFilled,
  InstagramFilled,
  InfoCircleFilled,
  EyeOutlined,
} from '@ant-design/icons'

import {
  Button,
  CheckboxGroup,
  CheckboxGroupProps,
  DatePicker,
  DatePickerProps,
  FileUpload,
  FileUploadProps,
  Input,
  InputProps,
  Password,
  RadioGroup,
  RadioGroupProps,
  Select,
  SelectProps,
  TextArea,
  TextAreaProps,
  Slider as AntSlider,
  Switch,
  Rate,
  Tag,
  DraggerUploader,
  DragFileUploadProps,
  AddInSelect,
  CommentBox,
  CommentBoxV2,
  DimensionsSelect,
  DimensionsSelectV2,
  PopOver,
  Modal,
  CustomRadio,
  RichText,
  Tooltip,
  useUtils,
} from '@pepper/ui'
import noop from '@pepper/utils/src/function/noop'
import { countWords } from '@pepper/utils/src/function/string'

import ComponentPicker from 'container/Projects/CreateProjectV2/components/ComponentPicker'
import { ReactComponent as LockFilled } from 'assets/images/lock.svg'
import { ReactComponent as TooltipInfo } from 'assets/images/mini_tooltip.svg'
import LanguageSelector from 'components/LanguageSelector/LanguageSelector'
import styles from './_.module.css'

const { RangePicker } = DatePicker

interface FieldProps {
  name: string
  error?: boolean
  label?: React.ReactNode | string
  helperText?: React.ReactNode | string
  className?: string
  size?: SizeType
  showError?: boolean
  onBlur?: React.EventHandler<React.FocusEvent>
  helperErrorText?: string
  required?: boolean
  style?: React.CSSProperties
  hasHoverEffect?: boolean
}

export type FieldComponent =
  | 'text'
  | 'textarea'
  | 'password'
  | 'tel'
  | 'multi-select'
  | 'multi-select-tags'
  | 'select'
  | 'list'
  | 'file'
  | 'checkbox'
  | 'radio'
  | 'boolean'
  | 'selection'
  | 'datepicker'
  | 'rangepicker'
  | 'html'
  | 'social'
  | 'blockSelect'
  | 'slider'
  | 'rate'
  | 'tagSelect'
  | 'dragDrop'
  | 'addInSelect'
  | 'addOptionSelect'
  | 'addDimensions'
  | 'comment-box'
  | 'selectDimension'
  | 'multiBlockSelect'
  | 'designBlockSelect'
  | 'addCustomColors'
  | 'customRadio'
  | 'colorBlockSelect'
  | 'languageSelect'
  | string
  | React.ReactNode

export function getFieldComponent<FormValues>({
  type,
  name,
  label,
  helperText,
  className = '',
  fieldProps,
  required,
  actions,
  defaultValue,
}: {
  type: FieldComponent
  name: string
  label?: string | React.ReactNode
  helperText?: string | ReactNode
  className?: string

  fieldProps?: any
  required?: boolean
  actions: FormikHelpers<FormValues>
  defaultValue?: string
}): ReactNode {
  switch (type) {
    case 'text':
      return (
        <TextField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          inputType={fieldProps?.inputType}
        />
      )
    case 'textarea':
      return (
        <TextAreaField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          loadV2={fieldProps?.loadV2}
        />
      )
    case 'password':
      return (
        <PasswordField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'tel':
      return (
        <PhoneField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'list':
      return (
        <ListField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'radio':
      return (
        <RadioField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          popoverInfo={fieldProps.popoverInfo}
        />
      )
    case 'checkbox':
      return (
        <CheckboxField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'select':
      return (
        <SelectField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          loadV2={fieldProps?.loadV2}
          actions={actions as FormikProps<any>}
        />
      )
    case 'multi-select':
      return (
        <SelectField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={{ ...fieldProps, mode: 'multiple' }}
        />
      )
    case 'multi-select-tags':
      return (
        <SelectField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={{ ...fieldProps, mode: 'tags' }}
          loadV2={fieldProps?.loadV2}
        />
      )
    case 'file':
      return (
        <FileUploadField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          setFieldValue={actions.setFieldValue}
        />
      )
    case 'html':
      console.error(
        'found a place where formfields html type is still being used, this breaks as we removed ckeditor',
        {
          type,
          name,
          label,
          helperText,
          className,
          fieldProps,
          required,
          actions,
        }
      )
      return null
    case 'editor':
      return (
        <RichEditor
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          setFieldValue={actions.setFieldValue}
          fieldProps={fieldProps}
        />
      )
    case 'selection':
      return (
        <SelectionField name={name} className={className} {...fieldProps} />
      )
    case 'datepicker':
      return (
        <DatePickerField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'rangepicker':
      return (
        <RangePickerField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'social':
      return (
        <AddLinkField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          setFieldValue={actions.setFieldValue}
          setFieldTouched={actions.setFieldTouched}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'blockSelect':
      return (
        <BlockSelect
          label={label}
          required={required}
          name={name}
          helperText={helperText}
          {...fieldProps}
        />
      )
    case 'slider':
      return (
        <Slider label={label} required={required} name={name} {...fieldProps} />
      )
    case 'switch':
    case 'boolean':
      return (
        <SwitchField
          label={label}
          required={required}
          name={name}
          {...fieldProps}
        />
      )
    case 'rate':
      return (
        <RatingField
          name={name}
          label={label}
          className={className}
          required={required}
        />
      )
    case 'tagSelect':
      return (
        <TagMultiSelect
          label={label}
          required={required}
          name={name}
          {...fieldProps}
        />
      )
    case 'dragDrop':
      return (
        <DragDropFileUploadField
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          setFieldValue={actions.setFieldValue}
          setFieldTouched={actions.setFieldTouched}
          fieldProps={fieldProps}
        />
      )
    case 'addOptionSelect':
      return (
        <AddOptionInSelect
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
          loadV2={fieldProps?.loadV2}
        />
      )
    case 'addDimensions':
      return (
        <DimensionsField
          name={name}
          label={label}
          required={required}
          className={className}
          helperText={helperText}
          setFieldValue={actions.setFieldValue}
          setFieldTouched={actions.setFieldTouched}
          popoverInfo={fieldProps.popoverInfo}
          loadV2={fieldProps?.loadV2}
        />
      )
    case 'comment-box':
      return (
        <CommentBoxField
          name={name}
          label={label}
          className={className}
          setFieldValue={actions.setFieldValue}
          required={required}
          fieldProps={fieldProps}
          helperText={helperText}
        />
      )
    case 'selectDimensions':
      return (
        <AddDimensionSelect
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'multiBlockSelect':
      return (
        <MultiSelectBlock
          label={label}
          required={required}
          name={name}
          helperText={helperText}
          {...fieldProps}
        />
      )
    case 'colorBlockSelect':
      return (
        <ColorBlocks
          label={label}
          required={required}
          name={name}
          helperText={helperText}
          {...fieldProps}
        />
      )
    case 'addCustomColors':
      return (
        <AddMultipleCustomColors
          name={name}
          label={label}
          className={className}
          helperText={helperText}
          setFieldValue={actions.setFieldValue}
          required={required}
          fieldProps={fieldProps}
          hideCustomColorLabel={fieldProps.hideCustomColorLabel}
        />
      )
    case 'customRadio':
      return (
        <CustomRadioField
          name={name}
          className={className}
          label={label}
          helperText={helperText}
          required={required}
          fieldProps={fieldProps}
        />
      )
    case 'languageSelect':
      return (
        <LanguageSelector
          title={label}
          name={name}
          defaultValue={defaultValue}
        />
      )

    default:
      throw new Error(`component of ${type} not available`)
  }
}

interface SelectFieldProps extends FieldProps {
  fieldProps: SelectProps
  loadV2?: boolean
}

export const AddOptionInSelect: React.FC<SelectFieldProps> = ({
  className = '',
  label,
  name,
  helperText,
  showError = true,
  required,
  fieldProps,
  loadV2,
}) => {
  const [field, meta, helpers] = useField(name)
  const errorClass =
    meta.touched && meta.error && !loadV2 ? styles.borderRed : ''
  const [newOptions, setNewOptions] = useState(fieldProps.options ?? [])

  const handleAddItem = (text: string) => {
    const _addOptions = [...newOptions, { label: text, value: text }]
    setNewOptions(_addOptions)
  }

  return (
    <div
      className={clsx(className, styles.selectField, {
        [styles.inputV2Wrap]: loadV2,
      })}
      data-cy={name}
    >
      <label>
        <span
          className={clsx(styles.labelText, {
            [styles.inputLabelV2]: loadV2,
          })}
        >
          {label} {required && <span className={styles.requiredText}>*</span>}
        </span>
        <AddInSelect
          className={clsx('w-100', errorClass, {
            [styles.addInWrap]: loadV2,
          })}
          optionFilterProp="label"
          onChange={(value: any): void => helpers.setValue(value)}
          onBlur={(): void => helpers.setTouched(true)}
          value={field.value}
          options={newOptions}
          addItem={handleAddItem}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* comment box */
interface CommentBoxFieldProps extends FieldProps {
  fieldProps: {
    className?: string
    popoverInfo?: string
    allowedFiles?: string[]
    showCount?: boolean
    maxLength?: number
    tagField?: string
    tag?: string
    loadV2?: boolean
    miniTooltip?: string
    updatePosition?: (val: number) => void
  }
  setFieldValue: (n: string, v: any) => void
  setFieldTouched?: (n: string) => void
}

export const CommentBoxField: React.FC<CommentBoxFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  setFieldValue,
  setFieldTouched = noop,
  fieldProps,
}) => {
  const [field, meta] = useField(name)
  const { error } = meta

  return (
    <div
      className={clsx(className, {
        [styles.inputV2Wrap]: fieldProps?.loadV2,
      })}
    >
      <label>
        <span
          className={clsx(styles.labelText, {
            [styles.inputLabelV2]: fieldProps?.loadV2,
          })}
        >
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {fieldProps?.miniTooltip && (
            <Tooltip
              placement="top"
              title={() => {
                return fieldProps.miniTooltip ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: fieldProps.miniTooltip,
                    }}
                  />
                ) : null
              }}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
          {fieldProps.popoverInfo && (
            <PopOver content={fieldProps.popoverInfo}>
              <InfoCircleFilled className={styles.infoIcon} />
            </PopOver>
          )}
        </span>
        {fieldProps?.loadV2 ? (
          <CommentBoxV2
            {...fieldProps}
            formData={field.value}
            onChange={(values: CommentBox) => {
              setFieldTouched(name)
              setFieldValue(name, values)
              if (fieldProps.tagField) {
                setFieldValue(fieldProps.tagField, values.comment)
              }
            }}
            showSendButton={false}
            className={styles.commentBoxContainer}
          />
        ) : (
          <CommentBox
            {...fieldProps}
            formData={field.value}
            onChange={(values: CommentBox) => {
              setFieldTouched(name)
              setFieldValue(name, values)
            }}
            showSendButton={false}
            className={styles.commentBoxContainer}
          />
        )}
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError &&
      meta.touched &&
      (
        error as unknown as {
          comment: string
        }
      )?.comment ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} />{' '}
          {
            (
              error as unknown as {
                comment: string
              }
            ).comment
          }
        </div>
      ) : null}
    </div>
  )
}

/* rate */
export const RatingField: React.FC<FieldProps> = ({
  className = '',
  label,
  name,
  helperText,
  showError = true,
  required,
  style,
}) => {
  const [field, meta, helpers] = useField<number>(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Rate
          style={style}
          value={field.value}
          onChange={value => helpers.setValue(value)}
          className={`${errorClass}`}
        />
      </label>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
      {helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}

/* text */
interface FieldInputProps extends InputProps {
  touched?: boolean
  loadV2?: boolean
  updatePosition?: (val: number) => void
  miniTooltip?: string
}

interface TextFieldProps extends FieldProps {
  fieldProps: FieldInputProps
  inputType?: string
  ref?: React.ForwardedRef<unknown>
}

export const TextField: React.FC<TextFieldProps> = React.forwardRef(
  (
    {
      className = '',
      label,
      name,
      helperText,
      helperErrorText,
      showError = true,
      required,
      fieldProps = {},
      inputType = 'text',
    },
    ref
  ) => {
    const [field, meta] = useField(name)
    const errorClass = meta.touched && meta.error ? styles.borderRed : ''
    return (
      <div
        className={clsx(className, {
          [styles.inputV2Wrap]: fieldProps?.loadV2,
        })}
      >
        <label>
          <span
            className={clsx(styles.labelText, {
              [styles.inputLabelV2]: fieldProps?.loadV2,
            })}
          >
            {label}
            {required && <span className={styles.requiredText}>*</span>}
            {fieldProps?.miniTooltip && (
              <Tooltip
                placement="top"
                title={() => {
                  return fieldProps.miniTooltip ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: fieldProps.miniTooltip,
                      }}
                    />
                  ) : null
                }}
              >
                <TooltipInfo className={styles.tooltipPos} />
              </Tooltip>
            )}
          </span>
          <Input
            itemRef={(ref ?? fieldProps?.itemRef) as unknown as string}
            {...field}
            {...fieldProps}
            type={inputType}
            className={clsx(errorClass, {
              [styles.inputAreaV2]: fieldProps?.loadV2,
            })}
          />
        </label>
        {helperText && <span className={styles.helperText}>{helperText}</span>}
        {showError && (meta.touched || fieldProps?.touched) && meta.error ? (
          <div className={styles.error}>
            <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
          </div>
        ) : null}
        {helperErrorText && (
          <div className={styles.helperText}>
            {meta.error || field.value === '' ? (
              <CloseCircleFilled className={styles.red} />
            ) : (
              <CheckCircleFilled className={styles.green} />
            )}
            {helperErrorText}
          </div>
        )}
      </div>
    )
  }
)

/* password */
interface PasswordFieldProps extends FieldProps {
  fieldProps: InputProps
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Password {...field} {...fieldProps} />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* tel */
interface PhoneFieldProps extends FieldProps {
  fieldProps: InputProps
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  required,
  helperText,
  fieldProps,
}) => {
  const [field, meta, helper] = useField<PhoneNumber | null>(name)
  const extension = field.value?.extension
  const number = field.value?.number
  const options = ['91']
  const selectBefore = (
    <Select
      className={styles.phoneExtensionSelect}
      options={options.map(option => ({ value: option, label: option }))}
      onChange={(value): void =>
        helper.setValue({
          extension: value || '',
          number: number || '',
        })
      }
      value={extension}
    />
  )

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Input
          addonBefore={selectBefore}
          onBlur={(): void => helper.setTouched(true)}
          onChange={(e): void =>
            helper.setValue({
              extension: extension || '',
              number: e.target.value,
            })
          }
          value={number}
          type="tel"
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* textarea */
interface FieldAreaInputProps extends TextAreaProps {
  updatePosition?: (val: number) => void
  miniTooltip?: string
}
interface TextAreaFieldProps extends FieldProps {
  fieldProps: FieldAreaInputProps
  loadV2?: boolean
  rows?: number
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
  loadV2,
  rows,
}) => {
  const [field, meta, helper] = useField(name)
  const updateFormFields = (_value: string) => {
    _value = _value.trim() !== '' ? _value : ''
    helper.setValue(_value)
  }
  return (
    <div
      className={clsx(className, {
        [styles.inputV2Wrap]: loadV2,
      })}
    >
      <label>
        <span
          className={clsx(styles.labelText, {
            [styles.inputLabelV2]: loadV2,
          })}
        >
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {fieldProps?.miniTooltip && (
            <Tooltip
              placement="top"
              title={() => {
                return fieldProps.miniTooltip ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: fieldProps.miniTooltip,
                    }}
                  />
                ) : null
              }}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
        </span>
        <TextArea
          {...field}
          {...fieldProps}
          onChange={e => updateFormFields(e.target.value)}
          className={clsx(
            {
              [styles.inputAreaV2]: loadV2,
            },
            fieldProps.className
          )}
          rows={rows}
        />
      </label>
      {helperText && (
        <span className={clsx(styles.helperText, 'helper-text')}>
          {helperText}
        </span>
      )}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* text list field  (on enter the text is be added to the list) */
interface ListFieldProps extends FieldProps {
  fieldProps?: InputProps
}

export const ListField: React.FC<ListFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta, helper] = useField<string[]>(name)
  const [value, setValue] = useState<string>('')

  const addItem = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    helper.setValue([...field.value, (event.target as HTMLInputElement).value])
    setValue('')
  }

  const removeItem = (index: number): void => {
    const result = [
      ...field.value?.slice(0, index),
      ...field.value?.slice(index + 1),
    ]
    helper.setValue(result)
  }

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Input
          onChange={(e): void => setValue(e.target.value)}
          onBlur={(): void => helper.setTouched(true)}
          onPressEnter={addItem}
          value={value}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      <ol className={styles.list}>
        {field.value?.map((item: string, index: number) => (
          <li key={index}>
            <span>
              {index + 1}.&nbsp;&nbsp;
              {item}
            </span>
            <Button size="small" type="text">
              <DeleteOutlined onClick={(): void => removeItem(index)} />
            </Button>
          </li>
        ))}
      </ol>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* selection field */
interface SelectionFieldProps extends FieldProps {
  options: {
    label: string
    value: string
    suffix?: string | React.ReactNode
    disabled?: boolean
  }[]
}

export const SelectionField: React.FC<SelectionFieldProps> = ({
  className = '',
  name,
  helperText,
  showError = true,
  options,
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={`${className} ${styles.selectionField}`}>
      {options.map(option => (
        <label
          key={option.value}
          className={field.value === option.value ? styles.selected : ''}
        >
          {!option.disabled && (
            <>
              {field.value === option.value ? (
                <CheckCircleFilled />
              ) : (
                <CheckCircleOutlined />
              )}
            </>
          )}
          <input
            className={styles.visiblityHidden}
            {...field}
            type="radio"
            value={option.value}
            disabled={option.disabled}
          />
          <div className={styles.labelValue}>
            <span>{option.label}</span> <span>{option.suffix}</span>
          </div>
        </label>
      ))}
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* select field */
interface SearchSelectFieldProps extends FieldProps {
  fieldProps: SelectProps & {
    body?: IProjectBody[] | undefined
    shouldShowSearchV2?: boolean
    defaultSearchHeader?: string
    searchPlaceHolder?: string
    miniTooltip?: string
    disabled?: boolean
    parentDependent?: boolean
    categoryId?: number
    shouldShowKeywordPicker?: boolean
  }
  defaultValue?: any
  loadV2?: boolean
  actions?: FormikProps<any>
  noMargin?: boolean
}

export const SelectField: React.FC<SearchSelectFieldProps> = ({
  className = '',
  label,
  name,
  required,
  showError = true,
  helperText,
  fieldProps,
  defaultValue,
  loadV2,
  actions,
  noMargin = false,
}) => {
  const [field, meta, helper] = useField(name)
  const { body } = fieldProps
  const checkTicked = (key: string | number) => {
    if (key === field.name) {
      return field.value
    }
  }
  const errorClass =
    meta.touched && meta.error && !loadV2 ? styles.borderRed : ''
  const errorV2Class =
    meta.touched && meta.error && loadV2 ? styles.borderRedWrap : ''

  const [searchValue, setSearchValue] = useState('')
  const searchHeader = useMemo(() => {
    if (searchValue) return `showing results for '${searchValue}'`.toUpperCase()
    return fieldProps?.defaultSearchHeader?.toUpperCase()
  }, [searchValue, fieldProps.defaultSearchHeader])

  const filterOption = useCallback((search, optionOrGroup) => {
    const isGroup = Array.isArray(optionOrGroup.options)
    if (isGroup) {
      return false
    }
    return optionOrGroup?.label
      ?.toLowerCase?.()
      ?.includes(search?.toLowerCase())
  }, [])

  const searchRelatedProps = useMemo(() => {
    if (!fieldProps.shouldShowSearchV2) return fieldProps

    return {
      ...fieldProps,
      searchValue,
      filterOption: fieldProps.filterOption ?? filterOption,
      onSearch: (value: string) => setSearchValue(value),
      options: [
        {
          label: searchHeader,
          options: fieldProps.options as NonNullable<
            Extract<typeof fieldProps.options, { value: string | number }>
          >,
        },
      ],
    }
  }, [fieldProps, searchValue, searchHeader, filterOption])

  const handleChange = useCallback(
    (value): void => {
      if (field.name === 'genre_id') {
        actions?.setFieldValue('sub_genre_id', null, true)
      }
      let formattedValue
      if (Array.isArray(value)) {
        formattedValue = value
          .map(v => (v.trim ? v.trim() : v))
          .filter(v => (typeof v === 'string' ? Boolean(v) : true))
      } else {
        formattedValue = value?.trim ? value.trim() : value
      }
      helper.setValue(formattedValue)
    },
    [actions, helper, field.name]
  )

  return (
    <>
      <div
        className={clsx(className, styles.selectField, {
          [styles.inputV2Wrap]: loadV2,
          [styles.noMargin]: noMargin,
        })}
        data-cy={name}
      >
        <label>
          <span
            className={clsx('label-text', styles.labelText, {
              [styles.inputLabelV2]: loadV2,
            })}
          >
            {label} {required && <span className={styles.requiredText}>*</span>}
            {fieldProps?.miniTooltip && (
              <Tooltip
                placement="top"
                title={() => {
                  return fieldProps.miniTooltip ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: fieldProps.miniTooltip,
                      }}
                    />
                  ) : null
                }}
              >
                <TooltipInfo className={styles.tooltipPos} />
              </Tooltip>
            )}
          </span>

          <Select
            className={clsx('w-100', errorV2Class, errorClass, {
              [styles.inputSelectV2]: loadV2,
              [styles.inputMultiSelectV2]:
                loadV2 && fieldProps?.mode === 'tags',
              [styles.shouldShowSearchV2]: fieldProps.shouldShowSearchV2,
            })}
            optionFilterProp="label"
            disabled={fieldProps.disabled}
            onChange={handleChange}
            onBlur={(): void => helper.setTouched(true)}
            value={field.value}
            placeholder={fieldProps.placeholder}
            defaultValue={defaultValue}
            {...searchRelatedProps}
            {...(fieldProps.mode === 'tags'
              ? {
                  tokenSeparators: [','],
                  dropdownStyle: { display: 'none' },
                }
              : {})}
          />
        </label>
        {helperText && <span className={styles.helperText}>{helperText}</span>}
        {showError && meta.touched && meta.error ? (
          <div className={styles.error}>
            <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
          </div>
        ) : null}
      </div>
      {body?.map(bodyItem => (
        <NestedFieldsInSelect
          key={bodyItem.id}
          fieldJson={bodyItem}
          actions={actions}
          parentFieldProps={fieldProps}
          getParentFieldValue={checkTicked}
        />
      ))}
    </>
  )
}

const NestedFieldsInSelect = ({
  fieldJson,
  actions,
  parentFieldProps,
  getParentFieldValue,
}: {
  fieldJson: IProjectBody
  actions?: FormikProps<any>
  parentFieldProps?: any
  getParentFieldValue: (key: string) => any
}) => {
  const { sub_genres } = useUtils()
  const canShowSubGenres = (
    parentValue: number,
    subIndustries: { value: number; label: string }[],
    categoryId?: number
  ) => {
    return parentValue && subIndustries.length > 0 && categoryId !== 4
  }

  const parentValue = getParentFieldValue(fieldJson.conditionalVisibility.key)
  const sub_industries = sub_genres
    .filter(genre => genre.parent_id === parentValue)
    .map(({ id, title }) => ({ value: id, label: title }))
  const canShowNestedField =
    parentValue &&
    canShowSubGenres(parentValue, sub_industries, parentFieldProps.categoryId)

  if (fieldJson.conditionalVisibility.key === 'genre_id') {
    fieldJson.options = sub_industries
  }

  return actions && canShowNestedField ? (
    <div className={styles.subBody}>
      <ComponentPicker key={fieldJson.id} actions={actions} data={fieldJson} />
    </div>
  ) : null
}

/* radio field */
interface RadioFieldProps extends FieldProps {
  fieldProps: RadioGroupProps
  popoverInfo?: string
}

export const RadioField: React.FC<RadioFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
  popoverInfo,
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {popoverInfo && (
            <PopOver content={popoverInfo}>
              <InfoCircleFilled className={styles.infoIcon} />
            </PopOver>
          )}
        </span>
        <RadioGroup {...field} {...fieldProps} />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* radio field */
interface CheckboxFieldProps extends FieldProps {
  fieldProps: CheckboxGroupProps
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta, helper] = useField<CheckboxValueType[]>(name)

  return (
    <div className={className}>
      <span>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <CheckboxGroup
          onChange={(values): void => helper.setValue(values)}
          value={field.value}
          {...fieldProps}
        />
      </span>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* datepicker */
interface DatePickerFieldProps extends FieldProps {
  fieldProps: DatePickerProps
}

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta, helpers] = useField<Date | null>(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <DatePicker
          onChange={(value): void => helpers.setValue(value)}
          value={field.value ? new Date(field.value) : undefined}
          onBlur={(): void => helpers.setTouched(true)}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* rangepicker */
interface RangePickerFieldProps extends FieldProps {
  fieldProps: any
}

export const RangePickerField: React.FC<RangePickerFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta, helpers] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <RangePicker
          onChange={(value): void => helpers.setValue(value)}
          value={field.value}
          onBlur={(): void => helpers.setTouched(true)}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

interface RichTextFieldProps extends FieldProps {
  setFieldValue: (n: string, v: any) => void
  fieldProps: any
  setFieldTouched?: (n: string) => void
}

/* Rich Text editor */
export const RichEditor = ({
  className = '',
  name,
  label,
  helperText,
  required,
  setFieldValue,
  fieldProps,
  setFieldTouched = noop,
}: RichTextFieldProps): React.ReactElement => {
  const [field, meta] = useField(name)

  const { isAttachFile } = fieldProps
  const { isVideoRecord } = fieldProps
  const isLoomField = isAttachFile && isVideoRecord
  const isTextOnly = !isAttachFile && !isLoomField

  const updateFieldValue = (
    values: RichTextValues | string,
    _charCount?: number,
    plainText?: string
  ): void => {
    setFieldTouched(name)
    setFieldValue(name, values)

    if (fieldProps.tagField && typeof values !== 'string') {
      setFieldValue(fieldProps.tagField, values?.richText)
    }

    if (name === 'short_form_copy' && typeof plainText === 'string') {
      setFieldTouched('word_count')
      setFieldValue('word_count', countWords(plainText))
    }
  }

  const fieldValue: RichTextValues = useMemo(() => {
    if (field.value) return field.value
    if (isLoomField) return { richText: '', attachments: [], loomUrls: [] }
    if (isTextOnly) return ''
  }, [field.value, isLoomField, isTextOnly])

  return (
    <div className={clsx(className, styles.editorWrap)}>
      <label>
        <span
          className={clsx(styles.labelText, {
            [styles.inputLabelV2]: fieldProps?.loadV2,
          })}
        >
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {fieldProps.popoverInfo && (
            <PopOver content={fieldProps.popoverInfo}>
              <InfoCircleFilled className={styles.infoIcon} />
            </PopOver>
          )}
          {fieldProps?.miniTooltip && (
            <Tooltip
              placement="top"
              title={() => {
                return fieldProps.miniTooltip ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: fieldProps.miniTooltip,
                    }}
                  />
                ) : null
              }}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
        </span>
      </label>
      <RichText
        {...fieldProps}
        placeholder={fieldProps.placeholder}
        onChange={updateFieldValue}
        fieldData={fieldValue}
        loomButtonId={name}
      />
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* file */
interface FileUploadFieldProps extends FieldProps {
  fieldProps: Omit<FileUploadProps, 'handleFiles'>
  setFieldValue: (n: string, v: any) => void
}

export const FileUploadField = ({
  className = '',
  name,
  label,
  helperText,
  required,
  fieldProps,
  setFieldValue,
}: FileUploadFieldProps): React.ReactElement => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelTextV2}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
      </label>
      <FileUpload
        {...fieldProps}
        handleFiles={(files): void => {
          setFieldValue(name, files)
        }}
        defaultFileList={field.value}
      />
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* drag and drop uploader */
interface DragDropFileUploadFieldProps extends FieldProps {
  fieldProps: Omit<DragFileUploadProps, 'handleFiles'>
  setFieldValue: (n: string, v: any) => void
  setFieldTouched?: (n: string, t: boolean) => void
  onChange?: (files: Attachment[]) => void
  minimal?: boolean
}

export const DragDropFileUploadField = ({
  className = '',
  name,
  label,
  helperText,
  required,
  setFieldValue,
  fieldProps,
  onChange,
  minimal,
}: DragDropFileUploadFieldProps): React.ReactElement => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
      </label>
      <DraggerUploader
        {...fieldProps}
        minimal={minimal}
        handleFiles={(files): void => {
          setFieldValue(name, files)
          if (onChange) onChange(files)
        }}
        defaultFileList={field.value}
      />
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* social */
interface AddLinkFieldProps extends FieldProps {
  setFieldValue: (n: string, v: string) => void
  setFieldTouched: (n: string, v: boolean) => void

  fieldProps?: any
  showIcon?: boolean
}

export const AddLinkField: React.FC<AddLinkFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  required,
  setFieldValue,
  setFieldTouched,
  fieldProps,
  showIcon = false,
}) => {
  const [field, meta] = useField(name)
  const [linkCount, setLinkCount] = useState(field.value?.length ?? 0)

  useEffect(() => {
    setLinkCount(field.value?.length ?? 0)
  }, [field.value])

  const removeField = (
    index: number,
    arrayHelpers: FieldArrayRenderProps
  ): void => {
    setLinkCount(linkCount - 1)
    arrayHelpers.remove(index)
  }

  const getSocialIcon = (link: string): React.ReactNode => {
    const props = { className: styles.socialPrefixIcon }
    if (link.includes('facebook.com')) return <FacebookFilled {...props} />
    if (link.includes('linkedin.com')) return <LinkedinFilled {...props} />
    if (link.includes('twitter.com')) return <TwitterSquareFilled {...props} />
    if (link.includes('instagram.com')) return <InstagramFilled {...props} />
    else return <span style={{ width: 16 }}></span>
  }

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
      </label>

      <Button
        className={styles.addLinkBtn}
        icon={<PlusOutlined />}
        onClick={(): void => setLinkCount(linkCount + 1)}
        type="link"
        size="small"
      >
        Add a URL
      </Button>

      <div className={styles.AddLinkInputField}>
        <FieldArray
          name={`${name}`}
          render={(arrayHelpers): React.ReactNode => (
            <>
              {[...Array(linkCount)].map((_, index) => (
                <Input
                  className={styles.socialField}
                  prefix={
                    showIcon ? getSocialIcon(field.value[index] ?? '') : null
                  }
                  onBlur={(): void => setFieldTouched(name, true)}
                  suffix={
                    <DeleteOutlined
                      className={styles.socialSuffixIcon}
                      onClick={(): void => removeField(index, arrayHelpers)}
                    />
                  }
                  value={field.value ? field.value[index] : ''}
                  onChange={(e): void =>
                    setFieldValue(`${name}.${index}`, e.target.value)
                  }
                  key={index}
                  {...fieldProps}
                />
              ))}
            </>
          )}
        />
      </div>

      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* Block Selection */

interface BlockSelectProps extends FieldProps {
  pageSize?: number
  showBrowseMoreLink?: boolean
  options: {
    id: number
    icon: string
    title: string
    suffix?: string | React.ReactNode
    disabled?: boolean
    pageSize?: number
    staticIcon?: React.ReactNode | string
    icon_set?: {
      blue: string
      default?: string
      gray?: string
    }
    tag?: string
  }[]
  onSelect?: (selectedOption: number) => void
}

export const BlockSelect: React.FC<BlockSelectProps> = ({
  showError = true,
  label,
  required,
  showBrowseMoreLink = true,
  options,
  name,
  pageSize = 8,
  onSelect,
}) => {
  const [field, meta, helpers] = useField(name)
  const [totalItemsToShow, setTotalItemsToShow] = useState<number>(pageSize)

  return (
    <div>
      <div>
        <label>
          <span className={styles.labelText}>
            {label}
            {required && <span className={styles.requiredText}>*</span>}
          </span>
        </label>
        <div className={styles.blocksSelection} data-cy={name}>
          {options.slice(0, totalItemsToShow).map(option => (
            <label
              key={option.id}
              className={clsx(
                styles.verticalBlock,
                field.value === option.id && styles.active,
                { [styles.disabled]: option.disabled }
              )}
              {...(field.value === option.id && {
                'data-cy': `${name}-active`,
              })}
            >
              {field.value === option.id ? (
                <CheckCircleFilled className={styles.verticalBlockCheck} />
              ) : null}
              {option.disabled ? (
                <LockFilled className={styles.verticalBlockLock} />
              ) : null}
              {option.tag ? (
                <div className={styles.tagContainer}>
                  <span className={styles.blockTag}>{option.tag}</span>
                </div>
              ) : null}
              {field.value === option.id ? (
                <img src={option.icon_set?.blue} alt={option.title} />
              ) : (
                <img
                  src={
                    option.icon_set?.gray ? option.icon_set?.gray : option.icon
                  }
                  alt={option.title}
                />
              )}
              <p className={styles.verticalBlogTitle}>{option.title}</p>
              <input
                className={styles.visiblityHidden}
                {...field}
                onChange={(): void => {
                  if (typeof onSelect === 'function') onSelect(option.id)

                  helpers.setValue(option.id)
                }}
                type="radio"
                value={option.id}
                disabled={option.disabled}
              />
            </label>
          ))}
        </div>
        {showBrowseMoreLink ? (
          <Button
            className={styles.blockSelect__button}
            onClick={() =>
              setTotalItemsToShow(p => {
                if (options.length <= totalItemsToShow) return pageSize
                else return p + pageSize
              })
            }
            size="small"
            type="link"
          >
            {`Browse ${options.length <= totalItemsToShow ? 'Less' : 'More'}`}
          </Button>
        ) : null}
      </div>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* Slider */
// TODO: should be generic
export const Slider: React.FC<FieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  required,
}) => {
  const [field, meta, helpers] = useField(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''

  const onChange = (value: number[]): void => {
    helpers.setValue(value)
  }
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Select
          value="male"
          options={[
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' },
          ]}
          bordered={false}
        />
        <div className={styles.rangeTitle}>Age Range</div>
        <AntSlider
          range={true}
          value={field.value}
          onChange={onChange}
          className={`${errorClass}`}
        />
      </label>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* boolean, switch */
export const SwitchField: React.FC<FieldProps> = ({
  label,
  name,
  showError = true,
  required,
}) => {
  const [field, meta, helpers] = useField<boolean>(name)

  return (
    <label>
      <span className={styles.labelText}>
        {label}
        {required && <span className={styles.requiredText}>*</span>}
      </span>
      <div className={styles.metaSwitch}>
        <p>No</p>
        <Switch
          className={styles.switchBtn}
          size="small"
          checked={field.value}
          onChange={value => helpers.setValue(value)}
        />
        <p>Yes</p>
      </div>

      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </label>
  )
}

/** Tag Select */
interface TagMultiSelectProps extends FieldProps {
  options: {
    id: number | string
    title: string
    color?: string
    icon?: string
  }[]
  showIcon?: boolean
}

export const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  showError = true,
  label,
  required,
  options,
  name,
  showIcon = false,
}) => {
  const [field, meta, helper] = useField(name)
  return (
    <div>
      <div>
        {label ? (
          <label>
            <span className={styles.labelText}>
              {label}
              {required && <span className={styles.requiredText}>*</span>}
            </span>
          </label>
        ) : (
          ''
        )}
        <div
          className={
            showIcon ? styles.iconBlocksSelection : styles.tagBlocksSelection
          }
        >
          {options.map(option => (
            <label key={option.id} className={styles.tagBlock}>
              {showIcon ? (
                <div
                  className={clsx(
                    styles.iconBlock,
                    field?.value?.includes(option.id.toString()) &&
                      styles.activeTag
                  )}
                >
                  <img src={option.icon} alt={option.title} />
                  <p className={styles.iconTitle}>{option.title}</p>
                </div>
              ) : (
                <>
                  <Tag
                    color={
                      field?.value?.includes(option.id.toString())
                        ? option.color
                        : 'default'
                    }
                  >
                    {option.title}
                  </Tag>
                </>
              )}
              <input
                className={styles.visibilityHidden}
                {...field}
                onChange={(e): void => {
                  const i = field?.value?.findIndex(
                    (val: string) => val === e.target.value
                  )
                  if (i === -1) {
                    helper.setValue([...field.value, e.target.value])
                  } else {
                    helper.setValue(
                      field.value.filter(
                        (val: string) => val !== e.target.value
                      )
                    )
                  }
                }}
                type="checkbox"
                value={option.id.toString()}
              />
            </label>
          ))}
        </div>
      </div>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

interface AddDimensions extends FieldProps {
  setFieldValue: (n: string, v: { width: string; height: string }[]) => void
  setFieldTouched: (n: string, v: boolean) => void

  fieldProps?: any
  showIcon?: boolean
  popoverInfo?: string
  loadV2?: boolean
}

export const DimensionsField: React.FC<AddDimensions> = ({
  className = '',
  label,
  name,
  showError = true,
  required,
  setFieldValue,
  fieldProps,
  popoverInfo = '',
  loadV2,
}) => {
  const [field, meta, helpers] = useField(name)
  const [linkCount, setLinkCount] = useState(field.value?.length ?? 0)

  useEffect(() => {
    setLinkCount(field.value?.length ?? 0)
  }, [field.value])

  const removeField = (
    index: number,
    arrayHelpers: FieldArrayRenderProps
  ): void => {
    setLinkCount(linkCount - 1)
    arrayHelpers.remove(index)
  }

  return (
    <div
      className={clsx(className, {
        [styles.inputV2Wrap]: loadV2,
      })}
    >
      <label>
        <span
          className={clsx(styles.labelText, {
            [styles.inputLabelV2]: loadV2,
          })}
        >
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {popoverInfo && (
            <PopOver content={popoverInfo}>
              <InfoCircleFilled className={styles.infoIcon} />
            </PopOver>
          )}
        </span>
      </label>

      <div>
        <FieldArray
          name={`${name}`}
          render={(arrayHelpers): React.ReactNode => (
            <>
              {field.value?.map((_: any, index: number) => (
                <div key={index} className={styles.dimensionFieldContainer}>
                  <div className={styles.inputContainer}>
                    <Input
                      className={clsx({
                        [styles.inputAreaV2]: loadV2,
                      })}
                      type={loadV2 ? 'number' : 'string'}
                      value={
                        field.value[index]?.width
                          ? field.value[index]?.width
                          : ''
                      }
                      placeholder="Enter Width"
                      onChange={(e): void => {
                        const _value = [...field.value]
                        let fieldValue = e.target.value
                        if (loadV2 && Number(fieldValue) < 0) {
                          fieldValue = '0'
                        }
                        _value[index] = {
                          ..._value[index],
                          width: fieldValue,
                        }
                        helpers.setValue(_value)
                      }}
                      {...fieldProps}
                    />
                    <div>X</div>
                    <Input
                      className={clsx({
                        [styles.inputAreaV2]: loadV2,
                      })}
                      type={loadV2 ? 'number' : 'string'}
                      value={
                        field.value[index]?.height
                          ? field.value[index]?.height
                          : ''
                      }
                      placeholder="Enter Height"
                      onChange={(e): void => {
                        const _value = [...field.value]
                        let fieldValue = e.target.value
                        if (loadV2 && Number(fieldValue) < 0) {
                          fieldValue = '0'
                        }
                        _value[index] = {
                          ..._value[index],
                          height: fieldValue,
                        }
                        helpers.setValue(_value)
                      }}
                      key={index}
                      {...fieldProps}
                    />
                    <div>px</div>
                  </div>
                  <div className={styles.dimensionsAction}>
                    <div className={styles.trashWrap}>
                      <DeleteOutlined
                        className={styles.deleteDimensionIcon}
                        onClick={(): void => removeField(index, arrayHelpers)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        />
      </div>

      <Button
        type="link"
        icon={<PlusOutlined />}
        onClick={(): void => {
          const minimumValue = loadV2 ? '0' : ''
          if (!Array.isArray(field.value)) {
            setFieldValue(name, [{ width: minimumValue, height: minimumValue }])
            return
          }

          setFieldValue(name, [
            ...field.value,
            { width: minimumValue, height: minimumValue },
          ])
        }}
      >
        Add More
      </Button>

      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

interface CustomDimensionFieldProps {
  loadV2?: boolean
}

interface DimensionFieldProps extends SelectProps, CustomDimensionFieldProps {}
interface SelectDimensionProps extends FieldProps {
  fieldProps: DimensionFieldProps
}

export const AddDimensionSelect: React.FC<SelectDimensionProps> = ({
  className = '',
  label,
  name,
  helperText,
  showError = true,
  required,
  fieldProps,
}) => {
  const [field, meta, helpers] = useField(name)
  const errorClass =
    meta.touched && meta.error && !fieldProps?.loadV2 ? styles.borderRed : ''
  const [newOptions, setNewOptions] = useState(fieldProps.options ?? [])

  const handleAddItem = (values: { width: string; height: string }) => {
    const _displayLabel = `Custom (${values.width} X ${values.height}) px`
    const _addOptions = [
      ...newOptions,
      { label: _displayLabel, value: _displayLabel },
    ]
    setNewOptions(_addOptions)
  }

  return (
    <div
      className={`${className} ${styles.selectField} ${styles.inputV2Wrap}`}
      data-cy={name}
    >
      <label>
        <span className={styles.labelTextV2}>
          {label} {required && <span className={styles.requiredText}>*</span>}
        </span>
        {fieldProps?.loadV2 ? (
          <DimensionsSelectV2
            className={`w-100 ${errorClass}`}
            optionFilterProp="label"
            disabled={fieldProps.disabled}
            setValue={(value: any): void => helpers.setValue(value)}
            onBlur={(): void => helpers.setTouched(true)}
            value={field.value}
            options={newOptions}
            addItem={handleAddItem}
          />
        ) : (
          <DimensionsSelect
            className={`w-100 ${errorClass}`}
            optionFilterProp="label"
            disabled={fieldProps.disabled}
            onChange={(value: any): void => helpers.setValue(value)}
            onBlur={(): void => helpers.setTouched(true)}
            value={field.value}
            options={newOptions}
            addItem={handleAddItem}
          />
        )}
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/** Multi block Select */
interface MultiSelectBlockProps extends FieldProps {
  options: {
    id: number | string
    title: string
    color?: string
    icon?: string
    type?: string
    full_size_src?: string
  }[]
  showIcon?: boolean
  onClick: (e: MouseEvent<HTMLInputElement>) => void
  popoverInfo?: string
  showPreview?: boolean
  loadV2?: boolean
  clearKey?: string
  disabled?: boolean
}

export const MultiSelectBlock: React.FC<MultiSelectBlockProps> = ({
  showError = true,
  label,
  required,
  options,
  name,
  showIcon = false,
  popoverInfo,
  onClick,
  showPreview = true,
  loadV2,
  clearKey,
  disabled,
}) => {
  const [, , _clearHelpers] = useField(clearKey || '')
  const [field, meta, helper] = useField(name)
  const [showFullImage, setShowFullImage] = useState<{
    visible: boolean
    option?: {
      id: number | string
      title: string
      color?: string
      icon?: string
      type?: string
      full_size_src?: string
    } | null
  }>({ visible: false, option: null })

  return (
    <div>
      <Modal
        visible={showFullImage.visible}
        footer={null}
        onCancel={() => setShowFullImage({ visible: false, option: null })}
        closable={true}
        width={630}
      >
        <img
          loading="lazy"
          alt={showFullImage.option?.title}
          src={showFullImage.option?.full_size_src}
          className={styles.previewImage}
        />
        <div className={styles.imageSrcContainer}>
          <span>View Image Source:</span>
          <div className={styles.imageSrcContainer}>
            <a
              href={showFullImage.option?.full_size_src}
              target="_blank"
              rel="noreferrer"
            >
              {showFullImage.option?.full_size_src}
            </a>
          </div>
        </div>
      </Modal>
      <div
        className={clsx({
          [styles.inputV2Wrap]: loadV2,
        })}
      >
        {label ? (
          <label>
            <span
              className={clsx(styles.labelText, {
                [styles.inputLabelV2]: loadV2,
              })}
            >
              {label}
              {required && <span className={styles.requiredText}>*</span>}
              {popoverInfo && (
                <PopOver content={popoverInfo}>
                  <InfoCircleFilled className={styles.infoIcon} />
                </PopOver>
              )}
            </span>
          </label>
        ) : (
          ''
        )}

        <div className={styles.ColourBlocksSelection}>
          {options.map(option => (
            <label
              key={option.id}
              className={clsx(
                styles.colorBlock,
                field.value?.includes(option.id) && styles.colourBlockActive
              )}
            >
              <div>
                {field.value?.includes(`${option.id}`) ? (
                  <CheckCircleFilled className={styles.verticalBlockCheck} />
                ) : null}
                <img src={option.icon} alt={option.title} />
              </div>

              {showPreview && (
                <div>
                  <EyeOutlined
                    className={styles.previewIcon}
                    onClick={() => setShowFullImage({ visible: true, option })}
                  />
                </div>
              )}

              <input
                className={clsx(styles.visibilityHidden, {
                  [styles.optionDisabled]: disabled,
                })}
                {...field}
                disabled={disabled}
                onChange={(e): void => {
                  let valueSet = field?.value ?? []
                  if (!Array.isArray(valueSet)) {
                    valueSet = []
                  }
                  if (
                    !valueSet.every((valueItem: any) =>
                      options.find(optionItem => optionItem.id === valueItem)
                    )
                  ) {
                    valueSet = []
                  }
                  const i = valueSet?.findIndex(
                    (val: string) => val === e.target.value
                  )
                  if (i === -1) {
                    helper.setValue([...valueSet, e.target.value])
                  } else {
                    helper.setValue(
                      valueSet.filter((val: string) => val !== e.target.value)
                    )
                  }
                  _clearHelpers.setValue([])
                }}
                onClick={onClick}
                type="checkbox"
                value={option.id.toString()}
              />
            </label>
          ))}
        </div>
      </div>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* Block Selection */
interface ColorBlockOption {
  id: number | string
  icon: string
  title: string
  suffix?: string | React.ReactNode
  disabled?: boolean
  pageSize?: number
  staticIcon?: React.ReactNode | string
  icon_set?: {
    blue: string
    default?: string
    gray?: string
  }
  image?: string
  tag?: string
}

interface ColorBlockSelectProps extends FieldProps {
  pageSize?: number
  showBrowseMoreLink?: boolean
  popoverInfo?: string
  options: ColorBlockOption[]
  onSelect?: (selectedOption: number | string) => void
  hasHoverEffect: boolean
  loadV2?: boolean
}

export const ColorBlocks: React.FC<ColorBlockSelectProps> = ({
  showError = true,
  label,
  required,
  showBrowseMoreLink,
  options,
  name,
  pageSize = 10,
  onSelect,
  hasHoverEffect = false,
  popoverInfo,
  loadV2,
}) => {
  const [field, meta, helpers] = useField(name)
  const [totalItemsToShow, setTotalItemsToShow] = useState<number>(pageSize)

  const getTemplateImage = (option: ColorBlockOption) => {
    if (loadV2 && option.image) {
      return option.image
    } else {
      return option.icon_set?.gray ? option.icon_set?.gray : option.icon
    }
  }
  return (
    <div>
      <div
        className={clsx({
          [styles.inputV2Wrap]: loadV2,
        })}
      >
        <label>
          <span
            className={clsx(styles.labelText, {
              [styles.inputLabelV2]: loadV2,
            })}
          >
            {label}
            {required && <span className={styles.requiredText}>*</span>}
            {popoverInfo && (
              <PopOver content={popoverInfo}>
                <InfoCircleFilled className={styles.infoIcon} />
              </PopOver>
            )}
          </span>
        </label>
        <div className={styles.ColourBlocksSelection} data-cy={name}>
          {options.map(option => (
            <div key={option.id}>
              <label
                className={clsx(
                  styles.colorBlock,
                  { [styles.box]: hasHoverEffect },
                  field.value === option.id && styles.colourBlockActive,

                  {
                    [styles.disabled]: option.disabled,
                  }
                )}
                {...(field.value === option.id && {
                  'data-cy': `${name}-active`,
                })}
              >
                {field.value === option.id ? (
                  <CheckCircleFilled className={styles.verticalBlockCheck} />
                ) : null}

                <div>
                  <img src={getTemplateImage(option)} alt={option.title} />

                  {hasHoverEffect && (
                    <div className={styles.hoverTitleBlock}>
                      <span>{option.title}</span>
                    </div>
                  )}

                  <input
                    className={styles.visiblityHidden}
                    {...field}
                    onChange={(): void => {
                      if (typeof onSelect === 'function') onSelect(option.id)

                      helpers.setValue(option.id)
                    }}
                    type="radio"
                    value={option.id}
                    disabled={option.disabled}
                  />
                </div>
              </label>
              {!hasHoverEffect && (
                <p className={styles.colorBlockTitle}>{option.title}</p>
              )}
            </div>
          ))}
        </div>
        {showBrowseMoreLink ? (
          <Button
            className={styles.blockSelect__button}
            onClick={() =>
              setTotalItemsToShow(p => {
                if (options.length <= totalItemsToShow) return pageSize
                else return p + pageSize
              })
            }
            size="small"
            type="link"
          >
            {`Browse ${options.length <= totalItemsToShow ? 'Less' : 'More'}`}
          </Button>
        ) : null}
      </div>
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

interface AddMultipleCustomColorsProps extends FieldProps {
  setFieldValue: (name: string, value: any) => void

  fieldProps?: any
  hideCustomColorLabel?: string
}

export const AddMultipleCustomColors: React.FC<
  AddMultipleCustomColorsProps
> = ({
  className = '',
  label,
  name,
  showError = true,
  required,
  setFieldValue,
  hideCustomColorLabel = false,
  fieldProps,
}) => {
  const [field, meta] = useField(name)
  const [linkCount, setLinkCount] = useState(field.value?.length ?? 1)
  const [showField, setShowField] = useState<boolean>(false)

  useEffect(() => {
    setLinkCount(field.value?.length ?? 1)
  }, [field.value])

  const removeField = (
    index: number,
    arrayHelpers: FieldArrayRenderProps
  ): void => {
    setLinkCount(linkCount - 1)
    arrayHelpers.remove(index)
  }

  useEffect(() => {
    if (hideCustomColorLabel) {
      setShowField(true)
    }
  }, [hideCustomColorLabel])

  return (
    <div className={className}>
      {!hideCustomColorLabel && (
        <div
          onClick={() => setShowField(true)}
          onKeyPress={() => setShowField(true)}
          tabIndex={0}
          role="button"
          className={styles.customColorContainer}
        >
          <span className={styles.specificColorLabel}>
            {label}
            {required && <span className={styles.requiredText}>*</span>}
          </span>
        </div>
      )}

      {showField ? (
        <div className={styles.fieldBlockContainer}>
          <div className={styles.labelText}>Enter Color Hex Code:</div>
          <div>
            <FieldArray
              name={`${name}`}
              render={(arrayHelpers): React.ReactNode => (
                <>
                  {[...Array(linkCount)].map((_: any, index: number) => (
                    <div key={index} className={styles.dimensionFieldContainer}>
                      <div className={styles.inputContainer}>
                        <Input
                          className={styles.colorPickerInputField}
                          type="color"
                          value={
                            field.value
                              ? `#${field.value[index]?.slice(1)}`
                              : '#'
                          }
                          onChange={(e): void => {
                            setFieldValue(`${name}.${index}`, e.target.value)
                          }}
                        />
                        <Input
                          value={field.value ? field.value[index] : ''}
                          onChange={(e): void => {
                            let _value = e.target.value
                            if (!_value.includes('#')) {
                              _value = `#${e.target.value}`
                            }
                            setFieldValue(`${name}.${index}`, _value)
                          }}
                          placeholder="#ff0000"
                          key={index}
                          {...fieldProps}
                        />

                        <div className={styles.trashWrap}>
                          <DeleteOutlined
                            className={styles.deleteDimensionIcon}
                            onClick={(): void =>
                              removeField(index, arrayHelpers)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            />
          </div>

          <Button
            className={styles.addLinkBtn}
            icon={<PlusOutlined />}
            onClick={(): void => setLinkCount(linkCount + 1)}
            type="link"
            size="small"
          >
            Add Color
          </Button>
        </div>
      ) : null}

      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* custom radio field */
interface CustomRadioFieldProps extends FieldProps {
  fieldProps: {
    options: CheckboxOptionType[]
  }
}

export const CustomRadioField: React.FC<CustomRadioFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps,
}) => {
  const [field, meta, helper] = useField(name)

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <CustomRadio
          {...field}
          {...fieldProps}
          customInput={field.value?.customInput ?? ''}
          onChange={e =>
            helper.setValue({ customInput: '', selectedOption: e.target.value })
          }
          value={field.value?.selectedOption ?? ''}
          setCustomInput={(value: string) =>
            helper.setValue({ ...field.value, customInput: value })
          }
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}
